import { SERVER_URL } from "../../../../utils";

const HANDLE_SIZE = 12;
const MIN_SIZE = 40;
/** Grosor de la zona de borde para redimensionar (franjas + hit-test); el interior queda para mover. */
const RESIZE_EDGE_STRIP_PX = 12;

const DEFAULT_GOOGLE_FONTS_BUNDLE =
	"https://fonts.googleapis.com/css2?family=Anton&family=Bebas+Neue&family=League+Spartan:wght@400;700&display=swap";

const isFontStylesheetUrl = (value) => /^https?:\/\//i.test(String(value || "").trim());

const parseGoogleFontsFamilies = (rawUrl) => {
	try {
		const url = new URL(String(rawUrl || "").trim());
		if (!/fonts\.googleapis\.com$/i.test(url.hostname)) return [];
		const families = [];
		const params = url.searchParams.getAll("family");
		params.forEach((fam) => {
			const base = decodeURIComponent(String(fam).split(":")[0] || "").replace(/\+/g, " ").trim();
			if (!base) return;
			families.push(`'${base}', sans-serif`);
		});
		return Array.from(new Set(families));
	} catch (_) {
		return [];
	}
};

const buildGoogleFontsCssUrl = (familyName) => {
	const clean = String(familyName || "")
		.trim()
		.replace(/^['"]+|['"]+$/g, "")
		.split(",")[0]
		.trim();
	if (!clean) return null;
	return `https://fonts.googleapis.com/css2?family=${encodeURIComponent(clean).replace(/%20/g, "+")}&display=swap`;
};

const categoryToGeneric = (category) => {
	const c = String(category || "").toLowerCase();
	if (c.includes("mono")) return "monospace";
	if (c.includes("hand")) return "cursive";
	if (c.includes("serif") && !c.includes("sans")) return "serif";
	return "sans-serif";
};

const buildFontFamilyStack = (familyName, category) => {
	const clean = String(familyName || "")
		.trim()
		.replace(/^['"]+|['"]+$/g, "")
		.split(",")[0]
		.trim();
	if (!clean) return null;
	return `'${clean}', ${categoryToGeneric(category)}`;
};

const importGoogleFontFamily = (familyName, category = "Sans Serif") => {
	const stack = buildFontFamilyStack(familyName, category);
	if (!stack) return { families: [], stylesheetUrls: [] };
	const url = buildGoogleFontsCssUrl(familyName);
	return { families: [stack], stylesheetUrls: url ? [url] : [] };
};

const importGoogleFontInput = (rawInput) => {
	const trimmed = String(rawInput || "").trim();
	if (!trimmed || !isFontStylesheetUrl(trimmed)) {
		return { families: [], stylesheetUrls: [] };
	}
	try {
		const url = new URL(trimmed);
		if (!/fonts\.googleapis\.com$/i.test(url.hostname)) {
			return { families: [], stylesheetUrls: [] };
		}
	} catch (_) {
		return { families: [], stylesheetUrls: [] };
	}
	const families = parseGoogleFontsFamilies(trimmed);
	return { families, stylesheetUrls: families.length > 0 ? [trimmed] : [] };
};

const mergeCustomFontEntries = (prev, { families = [], stylesheetUrls = [] }) => {
	const next = [...(prev || [])];
	[...families, ...stylesheetUrls].forEach((item) => {
		const v = String(item || "").trim();
		if (v && !next.includes(v)) next.push(v);
	});
	return next;
};

const getFontPickerOptions = (customFonts) =>
	(Array.isArray(customFonts) ? customFonts : [])
		.map((f) => String(f || "").trim())
		.filter((f) => f && !isFontStylesheetUrl(f));

const collectFontStylesheetUrls = (customFonts, extraUrls = []) => {
	const urls = new Set();
	[DEFAULT_GOOGLE_FONTS_BUNDLE, ...extraUrls].forEach((u) => {
		const s = String(u || "").trim();
		if (isFontStylesheetUrl(s)) urls.add(s);
	});
	(Array.isArray(customFonts) ? customFonts : []).forEach((entry) => {
		const s = String(entry || "").trim();
		if (!s) return;
		if (isFontStylesheetUrl(s)) {
			urls.add(s);
			return;
		}
		const built = buildGoogleFontsCssUrl(s);
		if (built) urls.add(built);
	});
	return Array.from(urls);
};

const injectEscapeRoomFontLinks = (urls) => {
	if (typeof document === "undefined") return () => {};
	const created = [];
	(urls || []).forEach((href) => {
		const h = String(href || "").trim();
		if (!isFontStylesheetUrl(h)) return;
		if (document.querySelector(`link[data-er-font="${h}"]`)) return;
		const link = document.createElement("link");
		link.rel = "stylesheet";
		link.href = h;
		link.setAttribute("data-er-font", h);
		document.head.appendChild(link);
		created.push(link);
	});
	return () => created.forEach((l) => l.remove());
};

const resizeHandleStyle = (pos, inset = false) => {
	const offset = inset ? 0 : -HANDLE_SIZE / 2;
	const isCorner = pos === "nw" || pos === "ne" || pos === "sw" || pos === "se";
	const isVerticalSide = pos === "n" || pos === "s";
	const isHorizontalSide = pos === "e" || pos === "w";
	return {
		position: "absolute",
		width: isCorner ? HANDLE_SIZE : isVerticalSide ? 22 : 10,
		height: isCorner ? HANDLE_SIZE : isVerticalSide ? 10 : 22,
		backgroundColor: "#7c3aed",
		border: "1px solid #ffffff",
		borderRadius: isCorner ? "50%" : 999,
		boxShadow: "0 1px 4px rgba(124,58,237,0.35)",
		zIndex: 30,
		cursor:
			pos === "se" || pos === "nw"
				? "nwse-resize"
				: pos === "sw" || pos === "ne"
				? "nesw-resize"
				: pos === "n" || pos === "s"
				? "ns-resize"
				: "ew-resize",
		...(pos === "nw" && { left: offset, top: offset }),
		...(pos === "ne" && { right: offset, top: offset }),
		...(pos === "sw" && { left: offset, bottom: offset }),
		...(pos === "se" && { right: offset, bottom: offset }),
		...(pos === "n" && { left: "50%", top: offset, transform: "translateX(-50%)" }),
		...(pos === "s" && { left: "50%", bottom: offset, transform: "translateX(-50%)" }),
		...(pos === "w" && { left: offset, top: "50%", transform: "translateY(-50%)" }),
		...(pos === "e" && { right: offset, top: "50%", transform: "translateY(-50%)" }),
	};
};

/** Si el clic cae cerca del borde del contenedor (pantalla), devuelve el handle n/s/e/w/...; si no, null. */
function hitTestResizeEdge(containerEl, clientX, clientY) {
	if (!containerEl || typeof containerEl.getBoundingClientRect !== "function") return null;
	const r = containerEl.getBoundingClientRect();
	const w = r.width;
	const h = r.height;
	if (w < 4 || h < 4) return null;
	const margin = Math.min(RESIZE_EDGE_STRIP_PX, Math.max(6, Math.min(w, h) * 0.18));
	const x = clientX - r.left;
	const y = clientY - r.top;
	const onLeft = x <= margin;
	const onRight = x >= w - margin;
	const onTop = y <= margin;
	const onBottom = y >= h - margin;
	if (onTop && onLeft) return "nw";
	if (onTop && onRight) return "ne";
	if (onBottom && onLeft) return "sw";
	if (onBottom && onRight) return "se";
	if (onTop) return "n";
	if (onBottom) return "s";
	if (onLeft) return "w";
	if (onRight) return "e";
	return null;
}

function resolveQuizImageUrl(url) {
	if (!url || !url.trim()) return null;
	if (url.startsWith("http") || url.startsWith("/") || url.startsWith("data:")) return url;
	return `${SERVER_URL}/${url}`;
}

function applyLockedAspectResize({ x, y, w, h, origX, origY, origW, origH, handle, minSize }) {
	const ratio = origW / origH;
	let nw = Math.max(minSize, w);
	let nh = Math.max(minSize, h);
	let nx = x;
	let ny = y;
	const hasE = handle.includes("e");
	const hasW = handle.includes("w");
	const hasS = handle.includes("s");
	const hasN = handle.includes("n");
	const isCorner = (hasE || hasW) && (hasN || hasS);

	if (!isCorner) {
		return { x: nx, y: ny, w: nw, h: nh };
	}

	const scaleW = nw / origW;
	const scaleH = nh / origH;
	if (Math.abs(scaleW - 1) >= Math.abs(scaleH - 1)) {
		nh = Math.max(minSize, nw / ratio);
	} else {
		nw = Math.max(minSize, nh * ratio);
	}

	if (hasW) nx = origX + (origW - nw);
	if (hasN) ny = origY + (origH - nh);

	return { x: nx, y: ny, w: nw, h: nh };
}

const REUSABLE_QUIZ_ID = "69b289e1d48bf507f06d25cc";

const shuffleArray = (arr) => {
	const a = [...arr];
	for (let i = a.length - 1; i > 0; i--) {
		const j = Math.floor(Math.random() * (i + 1));
		[a[i], a[j]] = [a[j], a[i]];
	}
	return a;
};

export {
	HANDLE_SIZE,
	MIN_SIZE,
	RESIZE_EDGE_STRIP_PX,
	DEFAULT_GOOGLE_FONTS_BUNDLE,
	parseGoogleFontsFamilies,
	buildGoogleFontsCssUrl,
	importGoogleFontFamily,
	buildFontFamilyStack,
	importGoogleFontInput,
	mergeCustomFontEntries,
	getFontPickerOptions,
	collectFontStylesheetUrls,
	injectEscapeRoomFontLinks,
	isFontStylesheetUrl,
	resizeHandleStyle,
	hitTestResizeEdge,
	applyLockedAspectResize,
	resolveQuizImageUrl,
	REUSABLE_QUIZ_ID,
	shuffleArray,
};
